// =============================================================
// Public agenda — home-page widget.
//
// One compact card per studio, rendered into the `#agendaMountXXX`
// element emitted by `agendaTpl()` in `src/lib/templates.ts`.
// Each card is a *poster* for the full booking tool at `/agenda`
// (see `src/lib/agendaApp.ts`): same vocabulary (day pills + hour
// list, status colors, Barlow Condensed micro-labels), no booking
// modal and no week navigation.
//
// Behaviour:
//   - Rolling 7-day window starting today (no Monday anchor — a
//     Monday-anchored week viewed on Sunday evening is ~100% dead).
//   - Contiguous hours with the same status are coalesced into one
//     row (e.g. `07:00–12:00 · Szabad · 5 óra`) so two cards × 14
//     hourly rows don't become a 1200px wall on the home page.
//   - Open slots are clickable `<a>`s into `/agenda`; the rest are
//     `<div>`s with a tooltip. No booking modal lives in the home
//     bundle.
//   - Re-renders on `onLocaleChange` so day names and status labels
//     update when the user switches language (the previous version
//     left them stale).
//   - When the caller passes `null` for events we render a shimmer
//     skeleton instead of classifying an empty array as "all free" —
//     the latter would flash an "everything is free" state for the
//     duration of the fetch.
//
// All untrusted content (event titles) is rendered via `textContent`
// and `setAttribute` — never `innerHTML` — so XSS is impossible by
// construction.
// =============================================================

import { t, getLocale, onLocaleChange } from './i18n';
import type { AgendaEvent } from './content';

const HOUR_START = 7;
const HOUR_END = 22;        // last slot starts at 21:00
const DAYS = 7;
const MAX_ROWS = 8;

// ---------- date helpers ----------
function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}
function addDays(d: Date, n: number): Date {
  const out = new Date(d);
  out.setDate(out.getDate() + n);
  return out;
}
function sameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}
function pad(n: number): string { return n < 10 ? '0' + n : '' + n; }

function dayNameKey(i: number): 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun' {
  return (['mon','tue','wed','thu','fri','sat','sun'] as const)[i];
}
function monthKey(m: number): 'jan' | 'feb' | 'mar' | 'apr' | 'may' | 'jun' | 'jul' | 'aug' | 'sep' | 'oct' | 'nov' | 'dec' {
  return (['jan','feb','mar','apr','may','jun','jul','aug','sep','oct','nov','dec'] as const)[m];
}

// ---------- DOM helper ----------
function el<K extends keyof HTMLElementTagNameMap>(tag: K, attrs: Record<string, unknown> = {}, ...kids: Array<Node | string | null | undefined | false>): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (k === 'class') node.className = String(v);
    else if (k === 'dataset') Object.assign(node.dataset, v as DOMStringMap);
    else if (k.startsWith('on') && typeof v === 'function') node.addEventListener(k.slice(2), v as EventListener);
    else if (v !== undefined && v !== null) node.setAttribute(k, String(v));
  }
  for (const k of kids) if (k) node.append(k);
  return node;
}

// ---------- types ----------
type SlotStatus = 'open' | 'event' | 'closed' | 'taken';

interface PublicSlot {
  hour: number;          // starting hour, 7..21
  status: SlotStatus;
  title?: string;
  eventId?: string;
  isPast: boolean;
}

interface SlotRun {
  startHour: number;
  endHour: number;       // exclusive
  hours: number;
  status: SlotStatus;
  title?: string;
  eventId?: string;
  isPast: boolean;
  exactStart: string;    // ISO timestamp from events (for tooltip)
  exactEnd: string;
}

interface StudioState {
  dayStart: Date;        // midnight today
  activeDay: number;     // 0..6 offset from dayStart
  events: AgendaEvent[];
}

// ---------- state ----------
const states = new Map<'A22' | 'M15', StudioState>();
const mounted = new Map<string, { location: 'A22' | 'M15' }>();
let localeWired = false;

function getOrCreateState(location: 'A22' | 'M15'): StudioState {
  let s = states.get(location);
  if (!s) {
    s = { dayStart: startOfDay(new Date()), activeDay: 0, events: [] };
    states.set(location, s);
  }
  return s;
}

function wireLocaleOnce(): void {
  if (localeWired) return;
  localeWired = true;
  onLocaleChange(() => {
    for (const [rootId, meta] of mounted) rerender(rootId, meta.location);
  });
}

// ---------- classification ----------
// Ported from `agendaApp.classify()` minus the booking logic (the
// public widget never shows "my bookings" — those live behind the
// login on `/agenda`).
function classifyHour(state: StudioState, location: 'A22' | 'M15', dayIdx: number, hour: number): PublicSlot {
  const date = addDays(state.dayStart, dayIdx);
  // DST-safe: compute the slot end via setHours, not +3600e3.
  const slotStart = new Date(date);
  slotStart.setHours(hour, 0, 0, 0);
  const slotEnd = new Date(date);
  slotEnd.setHours(hour + 1, 0, 0, 0);
  const slotTs = slotStart.getTime();
  const slotEndTs = slotEnd.getTime();
  const isPast = slotTs <= Date.now();

  // Match against admin events (open/event/closed/taken) plus the
  // public taken-slots list (synthetic entries with id starting
  // `taken:`).
  const hit = state.events.find(ev => {
    if (ev.location !== location) return false;
    const evS = new Date(ev.startsAt).getTime();
    const evE = new Date(ev.endsAt).getTime();
    // Half-open overlap: event covers the hour when it starts
    // before slot end AND ends after slot start. An event
    // `18:00–19:30` covers hours 18 and 19, but not 20.
    return evS < slotEndTs && evE > slotTs;
  });

  if (hit) {
    if (hit.kind === 'taken' || hit.id.startsWith('taken:')) {
      return { hour, status: 'taken', title: hit.title[getLocale()] || hit.title.hu, eventId: hit.id, isPast };
    }
    if (hit.kind === 'closed') {
      return { hour, status: 'closed', eventId: hit.id, isPast };
    }
    if (hit.kind === 'event') {
      return { hour, status: 'event', title: hit.title[getLocale()] || hit.title.hu, eventId: hit.id, isPast };
    }
    // kind === 'open' is an admin-defined "leave this hour bookable"
    // override — falls through to default-open.
  }

  if (isPast) {
    return { hour, status: 'closed', isPast };
  }
  return { hour, status: 'open', isPast };
}

// ---------- coalescing ----------
// Walk the classified hour array and merge adjacent hours into runs
// that share status + eventId + isPast. Keeps each card to ≤
// MAX_ROWS rows.
function coalesce(slots: PublicSlot[], events: AgendaEvent[], dayDate: Date): SlotRun[] {
  const runs: SlotRun[] = [];
  for (const slot of slots) {
    const last = runs[runs.length - 1];
    const same = last
      && last.status === slot.status
      && last.eventId === slot.eventId
      && last.isPast === slot.isPast;
    if (same && last) {
      last.endHour = slot.hour + 1;
      last.hours += 1;
    } else {
      // Compute the exact ISO range from the underlying event when
      // we have one, otherwise from the hour grid. Events that start
      // mid-hour surface their real time in the tooltip.
      const exact = computeExactRange(events, dayDate, slot.hour, slot.eventId);
      runs.push({
        startHour: slot.hour,
        endHour: slot.hour + 1,
        hours: 1,
        status: slot.status,
        title: slot.title,
        eventId: slot.eventId,
        isPast: slot.isPast,
        exactStart: exact.start,
        exactEnd: exact.end,
      });
    }
  }
  return runs;
}

function computeExactRange(events: AgendaEvent[], dayDate: Date, hour: number, eventId: string | undefined): { start: string; end: string } {
  if (eventId) {
    const ev = events.find(e => e.id === eventId);
    if (ev) return { start: ev.startsAt, end: ev.endsAt };
  }
  const s = new Date(dayDate);
  s.setHours(hour, 0, 0, 0);
  const e = new Date(dayDate);
  e.setHours(hour + 1, 0, 0, 0);
  return { start: s.toISOString(), end: e.toISOString() };
}

function formatRange(start: Date, end: Date, locale: 'hu' | 'en'): string {
  const months = locale === 'hu'
    ? ['január','február','március','április','május','június','július','augusztus','szeptember','október','november','december']
    : ['January','February','March','April','May','June','July','August','September','October','November','December'];
  if (start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()) {
    return `${start.getDate()}–${end.getDate()} ${months[start.getMonth()]} ${start.getFullYear()}`;
  }
  return `${start.getDate()} ${months[start.getMonth()]} – ${end.getDate()} ${months[end.getMonth()]} ${start.getFullYear()}`;
}

function isoDay(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function defaultActiveDay(state: StudioState): number {
  // If today has no non-past hour (late evening), jump to tomorrow
  // so the widget never opens on a dead day.
  for (let h = HOUR_START; h < HOUR_END; h++) {
    const slotStart = new Date(state.dayStart);
    slotStart.setHours(h, 0, 0, 0);
    if (slotStart.getTime() > Date.now()) return 0;
  }
  return 1;
}

// ---------- public API ----------
export function mountPublicAgenda(
  rootId: string,
  location: 'A22' | 'M15',
  allEvents: AgendaEvent[] | null,
): void {
  const root = document.getElementById(rootId);
  if (!root) return;
  wireLocaleOnce();

  const state = getOrCreateState(location);
  // Recompute the day anchor at mount — if the user kept the tab open
  // across midnight, the rolling window should slide forward.
  state.dayStart = startOfDay(new Date());
  if (allEvents !== null) {
    state.events = allEvents.filter(e => e.location === location);
    // Clamp any preserved activeDay back into the new 0..6 range.
    state.activeDay = Math.min(Math.max(0, state.activeDay), DAYS - 1);
    // First successful mount with no prior selection (activeDay === 0):
    // pick a sensible default. If today is all past (late evening),
    // advance to tomorrow. Preserved selections (1..6) are respected.
    if (state.activeDay === 0) {
      state.activeDay = defaultActiveDay(state);
    }
  }
  mounted.set(rootId, { location });

  render(rootId, location, state, allEvents === null);
}

function rerender(rootId: string, location: 'A22' | 'M15'): void {
  const state = states.get(location);
  if (!state) return;
  render(rootId, location, state, false);
}

// ---------- render ----------
function render(rootId: string, location: 'A22' | 'M15', state: StudioState, loading: boolean): void {
  const root = document.getElementById(rootId);
  if (!root) return;
  root.setAttribute('aria-busy', loading ? 'true' : 'false');
  root.replaceChildren();

  if (loading) {
    // 4 skeleton rows is enough to communicate "list of cards"
    // without committing to layout that will jump when the real
    // list arrives.
    for (let i = 0; i < 4; i++) {
      root.appendChild(el('div', { class: 'agenda-card__skeleton' }));
    }
    return;
  }

  // Window label in the card head (last day is +6).
  const headRange = document.getElementById(rootId === 'agendaMountA22' ? 'agendaRangeA22' : 'agendaRangeM15');
  if (headRange) {
    headRange.textContent = formatRange(state.dayStart, addDays(state.dayStart, DAYS - 1), getLocale() as 'hu' | 'en');
  }

  // ---- day pills (tablist) ----
  const today = new Date();
  const week = el('div', { class: 'agenda-card__week', role: 'tablist', 'aria-label': location });
  for (let d = 0; d < DAYS; d++) {
    const date = addDays(state.dayStart, d);
    const isToday = sameDay(date, today);
    const isActive = d === state.activeDay;
    const isTodayLabel = isToday && d === 0; // only the today-pill uses the localized "Ma/Today" name
    const dayBtn = el('button', {
      type: 'button',
      role: 'tab',
      class: 'agenda-card__day' + (isActive ? ' is-active' : '') + (isToday ? ' is-today' : ''),
      'data-day': String(d),
      'aria-selected': String(isActive),
      'aria-controls': `${rootId}-slots`,
      id: `${rootId}-day-${d}`,
      tabindex: isActive ? '0' : '-1',
    },
      el('span', { class: 'agenda-card__day-name' },
        isTodayLabel ? t('agenda.today') : t(`agendaApp.days.${dayNameKey((date.getDay() + 6) % 7)}`),
      ),
      el('span', { class: 'agenda-card__day-num' }, String(date.getDate())),
      el('span', { class: 'agenda-card__day-mon' }, t(`agendaApp.months.${monthKey(date.getMonth())}`)),
    );
    dayBtn.addEventListener('click', () => {
      state.activeDay = d;
      // Move focus to the clicked pill so keyboard users see where
      // they are, then re-render the slot list.
      render(rootId, location, state, false);
      const fresh = document.getElementById(`${rootId}-day-${d}`) as HTMLButtonElement | null;
      fresh?.focus();
    });
    week.appendChild(dayBtn);
  }
  root.appendChild(week);

  // Roving-tabindex keyboard navigation on the day strip.
  week.addEventListener('keydown', (ev: KeyboardEvent) => {
    const key = ev.key;
    let next: number | null = null;
    if (key === 'ArrowRight') next = Math.min(DAYS - 1, state.activeDay + 1);
    else if (key === 'ArrowLeft') next = Math.max(0, state.activeDay - 1);
    else if (key === 'Home') next = 0;
    else if (key === 'End') next = DAYS - 1;
    if (next === null || next === state.activeDay) return;
    ev.preventDefault();
    state.activeDay = next;
    render(rootId, location, state, false);
    const pill = document.getElementById(`${rootId}-day-${next}`) as HTMLButtonElement | null;
    pill?.focus();
  });

  // ---- slot list (tabpanel) ----
  const slotsRoot = el('div', {
    class: 'agenda-card__slots',
    id: `${rootId}-slots`,
    role: 'tabpanel',
    'aria-labelledby': `${rootId}-day-${state.activeDay}`,
  });

  const dayDate = addDays(state.dayStart, state.activeDay);
  const classified: PublicSlot[] = [];
  for (let h = HOUR_START; h < HOUR_END; h++) {
    classified.push(classifyHour(state, location, state.activeDay, h));
  }
  const runs = coalesce(classified, state.events, dayDate);

  if (runs.length === 0 || (runs.length === 1 && runs[0].status === 'closed' && runs[0].isPast)) {
    slotsRoot.appendChild(el('p', { class: 'agenda__empty' },
      state.activeDay === 0 ? t('agenda.noneToday') : t('agenda.emptyDay'),
    ));
  } else {
    const visible = runs.slice(0, MAX_ROWS);
    for (const run of visible) slotsRoot.appendChild(renderRun(run, location, dayDate));
    const hidden = runs.length - visible.length;
    if (hidden > 0) {
      const more = el('a', {
        class: 'agenda-card__more',
        href: `/${getLocale()}/agenda?loc=${location}&d=${isoDay(dayDate)}`,
        'data-nav': '',
        'data-slug': 'agenda',
      }, t('agenda.more', { count: hidden }));
      slotsRoot.appendChild(more);
    }
  }

  root.appendChild(slotsRoot);
}

function renderRun(run: SlotRun, location: 'A22' | 'M15', dayDate: Date): HTMLElement {
  const locale = getLocale() as 'hu' | 'en';
  const fmtTime = (d: Date) => d.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
  const startStr = fmtTime(new Date(run.exactStart));
  const endStr = fmtTime(new Date(run.exactEnd));

  const timeLabel = run.hours > 1 ? `${startStr}–${endStr}` : startStr;
  const statusLabel = slotLabel(run, locale);
  const tooltip = buildTooltip(run, dayDate, locale);

  const children = [
    el('span', { class: 'agenda-card__slot-time' }, timeLabel),
    el('span', { class: 'agenda-card__slot-label' }, statusLabel),
  ];
  if (run.hours > 1 && run.status === 'open') {
    children.push(el('span', { class: 'agenda-card__slot-count' }, t('agenda.hours', { count: run.hours })));
  } else if (run.hours > 1 && (run.status === 'closed' || run.status === 'event' || run.status === 'taken')) {
    // Still show the duration for non-open blocks so the row reads
    // as "this much time is locked", not "a single hour".
    children.push(el('span', { class: 'agenda-card__slot-count' }, t('agenda.hours', { count: run.hours })));
  }

  if (run.status === 'open') {
    const a = el('a', {
      class: `agenda-card__slot agenda-card__slot--${run.status}` + (run.isPast ? ' is-past' : ''),
      href: `/${getLocale()}/agenda?loc=${location}&d=${isoDay(dayDate)}`,
      'data-nav': '',
      'data-slug': 'agenda',
    }, ...children);
    if (tooltip) a.setAttribute('title', tooltip);
    return a;
  }
  const d = el('div', {
    class: `agenda-card__slot agenda-card__slot--${run.status}` + (run.isPast ? ' is-past' : ''),
  }, ...children);
  if (tooltip) d.setAttribute('title', tooltip);
  return d;
}

function slotLabel(run: SlotRun, _locale: 'hu' | 'en'): string {
  switch (run.status) {
    case 'open':   return t('agendaApp.legend.open');
    case 'event':  return run.title || t('agendaApp.legend.event');
    case 'closed': return t('agendaApp.legend.closed');
    case 'taken':  return t('agendaApp.legend.taken');
  }
}

function buildTooltip(run: SlotRun, dayDate: Date, locale: 'hu' | 'en'): string {
  const dateLabel = dayDate.toLocaleDateString(locale, { month: 'short', day: 'numeric' });
  const startStr = new Date(run.exactStart).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
  const endStr = new Date(run.exactEnd).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
  return `${dateLabel} · ${startStr}–${endStr} · ${slotLabel(run, locale)}`;
}